// a list of tokens by chain
import { Currency, Token } from '@uniswap/sdk-core'

import { SupportedNetworkId } from './networks'
import {
  AMPL,
  DAI,
  DAI_POLYGON,
  ETH2X_FLI,
  FEI,
  FRAX,
  FXS,
  nativeOnChain,
  renBTC,
  rETH2,
  sETH2,
  SWISE,
  TRIBE,
  USDC_MAINNET,
  USDC_POLYGON,
  USDT,
  USDT_POLYGON,
  WBTC,
  WBTC_POLYGON,
  WETH_POLYGON,
  WETH_POLYGON_MUMBAI,
  WRAPPED_NATIVE_CURRENCY,
} from './tokens'

type ChainTokenList = {
  readonly [networkId: number]: Token[]
}

type ChainCurrencyList = {
  readonly [networkId: number]: Currency[]
}

const WRAPPED_NATIVE_CURRENCIES_ONLY: ChainTokenList = Object.fromEntries(
  Object.entries(WRAPPED_NATIVE_CURRENCY)
    .map(([key, value]) => [key, [value]])
    .filter(Boolean)
)

// used to construct intermediary pairs for trading
export const BASES_TO_CHECK_TRADES_AGAINST: ChainTokenList = {
  ...WRAPPED_NATIVE_CURRENCIES_ONLY,
  [SupportedNetworkId.MAINNET]: [
    ...WRAPPED_NATIVE_CURRENCIES_ONLY[SupportedNetworkId.MAINNET],
    DAI,
    USDC_MAINNET,
    USDT,
    WBTC,
  ],
  [SupportedNetworkId.POLYGON]: [
    ...WRAPPED_NATIVE_CURRENCIES_ONLY[SupportedNetworkId.POLYGON],
    DAI_POLYGON,
    USDC_POLYGON,
    USDT_POLYGON,
    WETH_POLYGON,
  ],
}
export const ADDITIONAL_BASES: { [networkId: number]: { [tokenAddress: string]: Token[] } } = {
  [SupportedNetworkId.MAINNET]: {
    '0xF16E4d813f4DcfDe4c5b44f305c908742De84eF0': [ETH2X_FLI],
    [rETH2.address]: [sETH2],
    [SWISE.address]: [sETH2],
    [FEI.address]: [TRIBE],
    [TRIBE.address]: [FEI],
    [FRAX.address]: [FXS],
    [FXS.address]: [FRAX],
    [WBTC.address]: [renBTC],
    [renBTC.address]: [WBTC],
  },
}
/**
 * Some tokens can only be swapped via certain pairs, so we override the list of bases that are considered for these
 * tokens.
 */
export const CUSTOM_BASES: { [networkId: number]: { [tokenAddress: string]: Token[] } } = {
  [SupportedNetworkId.MAINNET]: {
    [AMPL.address]: [DAI, WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.MAINNET] as Token],
  },
}

/**
 * Shows up in the currency select for swap and add liquidity
 */
export const COMMON_BASES: ChainCurrencyList = {
  [SupportedNetworkId.MAINNET]: [
    nativeOnChain(SupportedNetworkId.MAINNET),
    DAI,
    USDC_MAINNET,
    USDT,
    WBTC,
    WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.MAINNET] as Token,
  ],
  [SupportedNetworkId.ROPSTEN]: [
    nativeOnChain(SupportedNetworkId.ROPSTEN),
    WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.ROPSTEN] as Token,
  ],
  [SupportedNetworkId.RINKEBY]: [
    nativeOnChain(SupportedNetworkId.RINKEBY),
    WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.RINKEBY] as Token,
  ],
  [SupportedNetworkId.GOERLI]: [
    nativeOnChain(SupportedNetworkId.GOERLI),
    WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.GOERLI] as Token,
  ],
  [SupportedNetworkId.POLYGON]: [
    nativeOnChain(SupportedNetworkId.POLYGON),
    WETH_POLYGON,
    USDC_POLYGON,
    DAI_POLYGON,
    USDT_POLYGON,
    WBTC_POLYGON,
  ],
  [SupportedNetworkId.POLYGON_MUMBAI]: [
    nativeOnChain(SupportedNetworkId.POLYGON_MUMBAI),
    WRAPPED_NATIVE_CURRENCY[SupportedNetworkId.POLYGON_MUMBAI] as Token,
    WETH_POLYGON_MUMBAI,
  ],
}

// used to construct the list of all pairs we consider by default in the frontend
export const BASES_TO_TRACK_LIQUIDITY_FOR: ChainTokenList = {
  ...WRAPPED_NATIVE_CURRENCIES_ONLY,
  [SupportedNetworkId.MAINNET]: [
    ...WRAPPED_NATIVE_CURRENCIES_ONLY[SupportedNetworkId.MAINNET],
    DAI,
    USDC_MAINNET,
    USDT,
    WBTC,
  ],
}
export const PINNED_PAIRS: { readonly [networkId: number]: [Token, Token][] } = {
  [SupportedNetworkId.MAINNET]: [
    [
      new Token(SupportedNetworkId.MAINNET, '0x5d3a536E4D6DbD6114cc1Ead35777bAB948E3643', 8, 'cDAI', 'Compound Dai'),
      new Token(
        SupportedNetworkId.MAINNET,
        '0x39AA39c021dfbaE8faC545936693aC917d5E7563',
        8,
        'cUSDC',
        'Compound USD Coin'
      ),
    ],
    [USDC_MAINNET, USDT],
    [DAI, USDT],
  ],
}
